<template>
  <div>
    <RepeaterInput
      v-if="field.type === FieldTypes.REPEATER"
      :rows="field.rows || []"
      :sub-fields="field.sub_fields || []"
      :row-title="field.repeater_row_title || null"
      :show-validation="showValidation"
      @on-input="(payload) => $emit('onRepeaterInput', payload)"
      @on-add-row="$emit('onRepeaterAddRow')"
      @on-remove-row="(rowIndex) => $emit('onRepeaterRemoveRow', rowIndex)"
      @on-row-value-change="(payload) => $emit('onRepeaterRowValueChange', payload)"
    />
    <component
      :is="component"
      v-else
      :value="field.value"
      :options="field.options"
      :placeholder="field.label"
      :assets="field.assets"
      :numeric="isNumericTextField"
      @on-input="(payload: string) => $emit('onInput', payload)"
      @on-assets="(payload) => $emit('onAssets', payload)"
    />
    <div v-if="showAdditionalInput">
      <TextInput
        :is-text-area="field.label === 'Beskriv händelseförloppet'"
        class="mt-16"
        :value="field.additional_value"
        type="text"
        :placeholder="field.label === 'Beskriv händelseförloppet' ? 'Beskriv händelsen…' : 'Annat'"
        @on-input="(payload: string) => $emit('onAdditionalInput', payload)"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { FieldTypes } from '@/constants/field-types';
import TextInput from '@/components/form-elements/TextInput.vue';
import CheckboxInput from '@/components/form-elements/ButtonSelectInput.vue';
import {
  IncidentField,
  IncidentRepeaterRow
} from '@/constants/types/api';
import RadioInput from '@/components/form-elements/RadioInput.vue';
import FileInput from '@/components/form-elements/FileInput.vue';
import SelectInput from '@/components/form-elements/SuggestionSelectInput.vue';
import RepeaterInput from '@/components/form-elements/RepeaterInput.vue';

type RepeaterRowValueChangePayload = {
  rowIndex: number;
  subFieldLabel: string;
  value: string | null;
};

const props = defineProps<{
  field: IncidentField;
  showValidation?: boolean;
  placeholder?: string;
  subText?: string;
  options?: unknown[];
  value?: string | { name: string; value: string; };
}>();

defineEmits<{
  (e: 'onInput', payload: string): void,
  (e: 'onAdditionalInput', payload: string): void,
  (e: 'onRepeaterInput', payload: IncidentRepeaterRow[]): void,
  (e: 'onRepeaterAddRow'): void,
  (e: 'onRepeaterRemoveRow', payload: number): void,
  (e: 'onRepeaterRowValueChange', payload: RepeaterRowValueChangePayload): void,
  (e: 'onAssets', payload: any[]): void,
}>();

const Fields = {
  [FieldTypes.TEXT]: TextInput,
  [FieldTypes.RADIO]: RadioInput,
  [FieldTypes.CHECKBOX]: CheckboxInput,
  [FieldTypes.SELECT]: SelectInput,
  [FieldTypes.FILE]: FileInput,
  [FieldTypes.MAP]: 'MapInput',
  [FieldTypes.WARNINGLISTCOMPANIES]: 'WarningListInput',
  [FieldTypes.PERPETRATOR]: 'PerpetratorInput',
  [FieldTypes.REPEATER]: RepeaterInput,
};

const component = computed(() => Fields[props.field.type]);

const isNumericTextField = computed(() => {
  return props.field.type === FieldTypes.TEXT && Boolean(props.field.numeric);
});

const showAdditionalInput = computed(() => {
  return (
    props.field.type !== FieldTypes.REPEATER &&
    props.field.allow_additional_value &&
    [FieldTypes.RADIO, FieldTypes.CHECKBOX, FieldTypes.SELECT].includes(props.field.type)
  );
});

</script>
